import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { scheduleApi } from "@/features/schedule/api/schedule";
import {
  COMPACT_ABBR,
  COMPACT_CELL,
  FULL_LABEL,
  getDayDisplayType,
} from "@/features/schedule/components/ScheduleCompactView";
import type { ScheduleDayInfo } from "@/features/schedule/types";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatDate } from "@/utils/formatDate";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import {
  EmptyState,
  milestoneMeta,
  PageHeader,
  TableShell,
  Th,
} from "../components/ui";
import { MilestoneDetailModal } from "../components/MilestoneDetailModal";
import { useChangeAnalysis, useChangeAnalysisPeople, useConnection } from "../hooks";
import type {
  ChangeAnalysisAttention,
  ChangeAnalysisMilestone,
  ChangeAnalysisProject,
  ChangeAnalysisProjectEvent,
  ChangeAnalysisProof,
  ChangeAnalysisResult,
  ChangeAnalysisTask,
  ChangeAnalysisWeek,
} from "../types";

// Verdict palette (the spec's thresholds); red comes from --color-primary.
const GREEN = "#15803d";
const AMBER = "#b45309";

const QUICK_CHIPS = [7, 14, 30];

// ── small date/text helpers ──
function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function daysAgoISO(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localISO(d);
}
/** "dd.MM" from an ISO date or datetime prefix. */
function ddmm(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}` : iso;
}
function ddmmyyyy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
/** "dd.MM–dd.MM" for a week starting at `monday`. */
function weekRangeLabel(monday: string): string {
  const [y, m, d] = monday.split("-").map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(start.getDate())}.${pad(start.getMonth() + 1)}–${pad(end.getDate())}.${pad(end.getMonth() + 1)}`;
}
/** Polish plural picker: plural(3, "zadanie", "zadania", "zadań"). */
function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const d = n % 10;
  const h = n % 100;
  return d >= 2 && d <= 4 && (h < 12 || h > 14) ? few : many;
}
/** Nazwy projektów niosą numery („EOP 51 …", „EOP 100 …") — numeric sprawia, że
 *  51 stoi przed 100, a nie odwrotnie jak przy zwykłym porównaniu tekstu. */
const byProjectName = (a: string, b: string) => a.localeCompare(b, "pl", { numeric: true });
const dayCount = (n: number) => (n === 1 ? "1 dzień" : `${n} dni`);
const afterDays = (n: number) => (n === 1 ? "po 1 dniu" : `po ${n} dniach`);

// ── shared bits ───────────────────────────────────────────────────────────────
function PanelHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-2.5">
      <h2 className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
        {children}
      </h2>
    </div>
  );
}

/** Skąd zadanie: ustalenie dyrekcji vs zapisek własny — nazwane WPROST w obu
 *  przypadkach (brak plakietki nie może znaczyć „własne") i w innym kolorze. */
function OriginBadge({ directorate, standalone }: { directorate: boolean; standalone?: boolean }) {
  return (
    <span
      className={`whitespace-nowrap border px-1 py-px align-middle text-[9px] uppercase tracking-wider ${
        standalone ? "" : "ml-1.5"
      }`}
      style={
        directorate
          ? {
              borderColor: "var(--color-text-primary)",
              color: "var(--color-text-primary)",
              fontWeight: 600,
            }
          : { borderColor: "#7c3aed", color: "#6d28d9", borderStyle: "dashed" }
      }
    >
      {directorate ? "ustalenie dyrekcji" : "zadanie własne"}
    </span>
  );
}

/** Stan kamienia w kolorach tablicy kamieni; `dense` = wersja do zdań na osi zmian. */
function StatePill({
  state,
  label,
  dense,
}: {
  state: string | null;
  label: string | null;
  dense?: boolean;
}) {
  if (!state || !label) return <span className="text-[var(--color-text-muted)]">—</span>;
  const meta = milestoneMeta(state as Parameters<typeof milestoneMeta>[0]);
  return (
    <span
      className={`inline-block whitespace-nowrap border align-middle font-mono uppercase tracking-wide ${
        dense ? "px-1 py-px text-[10px]" : "px-1.5 py-0.5 text-[10px]"
      }`}
      style={{ background: meta.bg, color: meta.fg, borderColor: meta.accent }}
    >
      {label}
    </span>
  );
}

/** One typed entry in the "Zrobione / Do omówienia" columns. */
function Entry({
  color,
  prefix,
  main,
  details,
  badge,
}: {
  color: string;
  prefix: string;
  main: string;
  details: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <li className="pl-2.5" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="text-xs text-[var(--color-text-primary)]">
        <span className="font-semibold">{prefix}</span> {main}
        {badge}
      </div>
      <div className="font-mono text-[11px] text-[var(--color-text-muted)]">{details}</div>
    </li>
  );
}

function ProofItem({ item }: { item: ChangeAnalysisProof }) {
  switch (item.type) {
    case "directive_done":
    case "own_task_done":
      return (
        <Entry
          color={GREEN}
          prefix={item.type === "directive_done" ? "Ustalenie wykonane:" : "Zadanie własne:"}
          main={item.description}
          badge={<OriginBadge directorate={item.type === "directive_done"} />}
          details={
            <>
              {item.project} · {formatDate(item.done_at)} · {afterDays(item.duration_days)}
              {item.late_days != null && item.late_days > 0 && (
                <span className="text-[var(--color-primary)]"> · {dayCount(item.late_days)} po terminie</span>
              )}
            </>
          }
        />
      );
    case "milestone_forward":
      return (
        <Entry
          color={GREEN}
          prefix="Kamień naprzód:"
          main={item.label}
          details={
            <>
              {item.project} · {formatDate(item.at)} ·{" "}
              <StatePill dense state={item.from} label={item.from_display} /> →{" "}
              <StatePill dense state={item.to} label={item.to_display} />
            </>
          }
        />
      );
  }
}

function AttentionItem({ item }: { item: ChangeAnalysisAttention }) {
  const red = "var(--color-primary)";
  switch (item.type) {
    case "directive_overdue":
      return (
        <Entry
          color={red}
          prefix="Ustalenie po terminie:"
          main={item.description}
          details={
            <>
              {item.project} · termin {formatDate(item.due_date)} ·{" "}
              <span className="text-[var(--color-primary)]">{dayCount(item.overdue_days)} po terminie</span>
              {item.due_moves > 0 && <span style={{ color: AMBER }}> · przesuwany ×{item.due_moves} *</span>}
            </>
          }
        />
      );
    case "directive_dodged":
      return (
        <Entry
          color={red}
          prefix="Termin przesuwany:"
          main={item.description}
          details={
            <>
              {item.project} · termin {formatDate(item.due_date)} ·{" "}
              <span style={{ color: AMBER }}>przesuwany ×{item.due_moves} *</span>
            </>
          }
        />
      );
    case "milestone_stalled":
      return (
        <Entry
          color={red}
          prefix="Kamień bez postępu:"
          main={item.label}
          details={
            <>
              {item.project} · bez zmian — {dayCount(item.stalled_days)}
              {item.overdue_days != null && item.overdue_days > 0 && (
                <span className="text-[var(--color-primary)]"> · {dayCount(item.overdue_days)} po terminie</span>
              )}
            </>
          }
        />
      );
    case "awaiting_nudge":
      return (
        <Entry
          color={red}
          prefix="Do ponaglenia:"
          main={item.label}
          details={
            <>
              {item.project} · czeka: {item.waiting_label} — {dayCount(item.waiting_days)} (SLA {item.sla_days})
            </>
          }
        />
      );
    case "milestone_backward":
      return (
        <Entry
          color={red}
          prefix="Kamień cofnięty:"
          main={item.label}
          details={
            <>
              {item.project} · {formatDate(item.at)}
              {item.note && <> · „{item.note}”</>}
            </>
          }
        />
      );
    case "project_silent":
      return (
        <Entry
          color={red}
          prefix="Projekt bez postępów:"
          main={item.project}
          details={
            <>
              {item.contract_number || "—"}
              {item.added_only_count > 0 && (
                <>
                  {" "}· dodano {item.added_only_count}{" "}
                  {plural(item.added_only_count, "zadanie", "zadania", "zadań")} — bez wykonania
                </>
              )}
            </>
          }
        />
      );
  }
}

// ── pasek obecności (port 1:1 z Harmonogramu) ─────────────────────────────────
const WEEKDAY_ABBR = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];
/** Skrót → etykieta w legendzie; rysujemy tylko to, co w oknie wystąpiło. */
const LEGEND: [string, string][] = [
  ["present", "w biurze"],
  ["uz_present", "w biurze (UZ)"],
  ["remote", "zdalnie"],
  ["remote_pending", "zdalnie (oczekuje)"],
  ["leave", "urlop"],
  ["sick_leave", "L4"],
  ["sick_leave_pending", "L4 (oczekuje)"],
  ["uz_absent_explicit", "nieobecny"],
];

/** Zakresy ≤ 35 dni — backend Harmonogramu ucina dłuższe w ciszy (ScheduleView.list),
 *  więc długi okres analizy pobieramy kawałkami i sklejamy. */
function scheduleChunks(sinceISO: string, untilISO: string, max = 35): { start: string; end: string }[] {
  const out: { start: string; end: string }[] = [];
  const end = new Date(`${untilISO}T00:00:00`);
  let cursor = new Date(`${sinceISO}T00:00:00`);
  // Bezpiecznik: 12 kawałków ≈ 14 miesięcy — dalej pasek i tak przestaje być czytelny.
  while (cursor <= end && out.length < 12) {
    const chunkEnd = new Date(cursor);
    chunkEnd.setDate(chunkEnd.getDate() + max - 1);
    out.push({ start: localISO(cursor), end: localISO(chunkEnd > end ? end : chunkEnd) });
    cursor = new Date(chunkEnd);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

function PresenceBar({
  personId,
  personName,
  sinceISO,
  untilISO,
}: {
  personId: string;
  personName: string;
  sinceISO: string;
  untilISO: string;
}) {
  const chunks = useMemo(() => scheduleChunks(sinceISO, untilISO), [sinceISO, untilISO]);
  const results = useQueries({
    // Te same klucze co useSchedule w module Harmonogramu — cache jest współdzielony.
    queries: chunks.map((c) => ({
      queryKey: ["schedule", c.start, c.end, false],
      queryFn: () => scheduleApi.getSchedule(c.start, c.end),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const loading = results.some((r) => r.isLoading);
  const days = useMemo(() => {
    const acc: { date: string; info: ScheduleDayInfo | undefined; holiday: string | null }[] = [];
    for (const r of results) {
      const data = r.data;
      if (!data) continue;
      const me = data.users.find((u) => u.id === personId);
      for (const d of data.days) {
        acc.push({ date: d.date, info: me?.days[d.date], holiday: d.is_holiday ? d.holiday_name : null });
      }
    }
    acc.sort((a, b) => a.date.localeCompare(b.date));
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.dataUpdatedAt).join(","), personId]);

  const todayISO = localISO(new Date());
  // Do ~6 tygodni każdy dzień ma miejsce na własny podpis; dalej podpisujemy tygodnie.
  const dense = days.length <= 45;
  const known = days.filter((d) => d.info);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const d of known) {
      const key = getDayDisplayType(d.info!);
      c[key] = (c[key] ?? 0) + 1;
    }
    return c;
  }, [known]);

  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 border-t border-[var(--color-border)] pt-3">
        <Spinner size="sm" />
        <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
          Wczytuję obecność z Harmonogramu…
        </span>
      </div>
    );
  }
  if (known.length === 0) {
    return (
      <div className="mt-3 border-t border-[var(--color-border)] pt-3 font-mono text-[11px] text-[var(--color-text-muted)]">
        Brak danych obecności dla tej osoby — Harmonogram pokazuje tylko Twój oddział i osoby
        z przyznanym podglądem.
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-[var(--color-border)] pt-3">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="section-label">Obecność w tym okresie — {personName}</span>
        <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
          {LEGEND.filter(([k]) => counts[k])
            .map(([k, label]) => `${label} ${counts[k]}`)
            .join(" · ") || "brak dni roboczych w okresie"}
        </span>
      </div>
      {/* JEDEN ciągły pasek na całą szerokość: dzień = segment, tygodnie rozdziela
          jasna kreska, daty podpisane pod poniedziałkami. Weekend/dzień wolny to
          jasne pole, nie dziura — inaczej oś czasu się rwie. */}
      <div className="flex h-7 w-full overflow-hidden border border-[var(--color-border-strong)]">
        {days.map((d, i) => {
          const dt = new Date(`${d.date}T00:00:00`);
          const key = d.info ? getDayDisplayType(d.info) : null;
          const cell = key ? COMPACT_CELL[key] : undefined;
          const off = !cell || cell.bg === "bg-transparent";
          const monday = (dt.getDay() + 6) % 7 === 0;
          const label = key ? FULL_LABEL[key] ?? key : "brak danych";
          return (
            <div
              key={d.date}
              title={`${formatDate(d.date)} · ${label}${d.holiday ? ` · ${d.holiday}` : ""}${
                d.info?.note ? ` — ${d.info.note}` : ""
              }`}
              className={`flex min-w-0 flex-1 items-center justify-center text-[10px] font-bold leading-none ${
                off ? "text-[var(--color-text-muted)]" : `${cell.bg} ${cell.text}`
              } ${d.date === todayISO ? "ring-1 ring-inset ring-[var(--color-primary)]" : ""}`}
              style={{
                background: off ? "var(--color-bg-elevated)" : undefined,
                borderLeft:
                  i === 0
                    ? undefined
                    : monday
                      ? "1px solid var(--color-border-strong)"
                      : "1px solid var(--color-bg-surface)",
              }}
            >
              {key ? COMPACT_ABBR[key] ?? "" : ""}
            </div>
          );
        })}
      </div>
      {/* Oś dat: przy krótkim okresie podpisujemy KAŻDY dzień (dzień tygodnia +
          data), przy długim — tylko poniedziałki, bo etykiety by się zlały. */}
      <div className="flex w-full">
        {days.map((d) => {
          const dt = new Date(`${d.date}T00:00:00`);
          const dow = (dt.getDay() + 6) % 7;
          const show = dense || dow === 0;
          const weekend = dow > 4;
          return (
            <div key={d.date} className="min-w-0 flex-1 pt-0.5 text-center leading-tight">
              {show && (
                <>
                  <div
                    className={`font-mono text-[9px] ${
                      weekend || d.holiday
                        ? "text-[var(--color-primary)]"
                        : "text-[var(--color-text-muted)]"
                    }`}
                  >
                    {WEEKDAY_ABBR[dow]}
                  </div>
                  <div
                    className={`font-mono text-[9px] ${
                      d.date === todayISO
                        ? "font-bold text-[var(--color-text-primary)]"
                        : "text-[var(--color-text-muted)]"
                    }`}
                  >
                    {dense ? dt.getDate() : ddmm(d.date)}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {LEGEND.filter(([k]) => counts[k]).map(([k, label]) => {
          const cell = COMPACT_CELL[k];
          return (
            <span key={k} className="flex items-center gap-1 font-mono text-[10px] text-[var(--color-text-muted)]">
              <span
                className={`inline-flex h-3.5 w-3.5 items-center justify-center border text-[8px] font-bold ${cell.bg} ${cell.text} ${cell.border}`}
              >
                {COMPACT_ABBR[k]}
              </span>
              {label}
            </span>
          );
        })}
        <span className="flex items-center gap-1 font-mono text-[10px] text-[var(--color-text-muted)]">
          <span className="inline-block h-3.5 w-3.5 border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)]" />
          dzień wolny
        </span>
      </div>
    </div>
  );
}

// ── „Rozliczenie okresu" ──────────────────────────────────────────────────────
function Stat({
  value,
  label,
  sub,
  color,
  title,
  subTitle,
}: {
  value: React.ReactNode;
  label: string;
  sub: React.ReactNode;
  color: string;
  title?: string;
  subTitle?: string;
}) {
  return (
    <div className="px-4 py-3" title={title}>
      <div className="font-mono text-2xl font-bold leading-none" style={{ color }}>
        {value}
      </div>
      <div className="section-label mt-1.5">{label}</div>
      <div className="mt-0.5 truncate font-mono text-[11px] text-[var(--color-text-muted)]" title={subTitle}>
        {sub}
      </div>
    </div>
  );
}

function VerdictPanel({ data }: { data: ChangeAnalysisResult }) {
  const v = data.verdict;
  const d = v.directives;
  const pctColor =
    d.pct == null
      ? "var(--color-text-muted)"
      : d.pct >= 80
        ? GREEN
        : d.pct >= 40
          ? AMBER
          : "var(--color-primary)";
  const inactive = data.projects.filter((p) => !p.active);
  const inactiveNames = inactive.map((p) => p.name || p.contract_number || "—").join(", ");
  const noProjects = v.projects_total === 0;
  const balanceColor = noProjects
    ? "var(--color-text-muted)"
    : v.attention === 0
      ? GREEN
      : v.attention > v.proofs
        ? "var(--color-primary)"
        : AMBER;

  return (
    <section className="card mb-4">
      <PanelHeader>Rozliczenie okresu — {data.person.name}</PanelHeader>
      <div className="grid grid-cols-1 divide-y divide-[var(--color-border)] border-b border-[var(--color-border)] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <Stat
          value={d.pct == null ? "—" : `${d.pct}%`}
          color={pctColor}
          label="Ustalenia dyrekcji wykonane"
          sub={`${d.done} z ${d.settled} wymagalnych${
            d.dodged > 0 ? ` · ${d.dodged} z przesuwanym terminem` : ""
          }`}
          title="Wymagalne = wykonane w tym okresie, zaległe na dziś (także sprzed okresu), albo z terminem przesuwanym w tym okresie. Liczy zadania dodane danej osobie przez dyrekcję."
        />
        <Stat
          value={`${v.projects_active}/${v.projects_total}`}
          color={
            noProjects
              ? "var(--color-text-muted)"
              : inactive.length > 0
                ? "var(--color-primary)"
                : "var(--color-text-primary)"
          }
          label="Projekty z postępem"
          sub={noProjects ? "brak żywych projektów ZE" : inactiveNames || "wszystkie z postępem"}
          subTitle={inactiveNames || undefined}
        />
        <Stat
          value={`${v.proofs} : ${v.attention}`}
          color={balanceColor}
          label="Bilans okresu"
          sub="zrobione : do omówienia"
        />
      </div>
      <div className="grid grid-cols-1 divide-y divide-[var(--color-border)] lg:grid-cols-2 lg:divide-x lg:divide-y-0">
        <div className="p-4">
          <h3 className="mb-2 font-mono text-[0.7rem] font-bold uppercase tracking-widest" style={{ color: GREEN }}>
            Zrobione w tym okresie ({data.proofs.length})
          </h3>
          {data.proofs.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">Nic do wykazania w tym okresie.</p>
          ) : (
            <ul className="space-y-2">
              {data.proofs.map((p, i) => (
                <ProofItem key={i} item={p} />
              ))}
            </ul>
          )}
        </div>
        <div className="p-4">
          <h3
            className="mb-2 font-mono text-[0.7rem] font-bold uppercase tracking-widest"
            style={{ color: "var(--color-primary)" }}
          >
            Do omówienia ({data.attention.length})
          </h3>
          {data.attention.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">Nic do omówienia — czysto.</p>
          ) : (
            <ul className="space-y-2">
              {data.attention.map((a, i) => (
                <AttentionItem key={i} item={a} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// ── „Co się zmieniło — projektami" ────────────────────────────────────────────
function EventLine({ e }: { e: ChangeAnalysisProjectEvent }) {
  const stamp = (
    <span className="text-[var(--color-text-muted)]">
      {formatDate(e.at)} · {e.by} ·{" "}
    </span>
  );
  if (e.kind === "milestone") {
    return (
      <li
        className="pl-2.5 font-mono text-[11px] text-[var(--color-text-primary)]"
        style={{ borderLeft: `3px solid ${e.forward ? GREEN : "var(--color-primary)"}` }}
      >
        {stamp}
        <b className="font-semibold">{e.label}</b>:{" "}
        <StatePill dense state={e.from} label={e.from_display} /> →{" "}
        <StatePill dense state={e.to} label={e.to_display} />
        {!e.forward && e.note && <span className="text-[var(--color-primary)]"> · „{e.note}”</span>}
      </li>
    );
  }
  if (e.kind === "task_done") {
    return (
      <li
        className="pl-2.5 font-mono text-[11px] text-[var(--color-text-secondary)]"
        style={{ borderLeft: "3px solid transparent" }}
      >
        {stamp}wykonane zadanie: {e.description}
        {e.late_days != null && e.late_days > 0 && (
          <span className="text-[var(--color-primary)]"> ({dayCount(e.late_days)} po terminie)</span>
        )}
        <OriginBadge directorate={e.by_directorate} />
      </li>
    );
  }
  return (
    <li
      className="pl-2.5 font-mono text-[11px] text-[var(--color-text-secondary)]"
      style={{ borderLeft: "3px solid transparent" }}
    >
      {stamp}dodane zadanie: {e.description}
      <OriginBadge directorate={e.by_directorate} />
    </li>
  );
}

function ProjectBlock({ p, showAvailable }: { p: ChangeAnalysisProject; showAvailable: boolean }) {
  // Backend already orders newest-first by day (done_at is day-precision, so `at`
  // mixes date-only and datetime strings — do not re-sort lexicographically here).
  const events = p.events;
  return (
    // Osobna kafla na projekt — na spotkaniu przechodzi się projekt po projekcie,
    // więc granica musi być widoczna z drugiego końca stołu.
    <div
      className="border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)]"
      style={p.active ? undefined : { borderColor: "var(--color-primary)" }}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2"
        style={{
          borderColor: p.active ? "var(--color-border)" : "var(--color-primary)",
          background: p.active ? "var(--color-bg-elevated)" : "var(--color-primary-dim)",
        }}
      >
        <div className="min-w-0">
          <span className="text-sm font-bold text-[var(--color-text-primary)]">{p.name}</span>
          <span className="ml-2 font-mono text-[11px] text-[var(--color-text-muted)]">
            {p.contract_number || "—"}
            {p.branch ? ` · ${p.branch}` : ""}
          </span>
        </div>
        {p.active ? (
          <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
            {p.events.length} {plural(p.events.length, "zdarzenie", "zdarzenia", "zdarzeń")}
          </span>
        ) : (
          <span className="bg-[var(--color-primary)] px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white">
            Brak postępów
          </span>
        )}
      </div>
      <div className="px-4 py-3">
      {p.active ? (
        <ul className="space-y-1.5">
          {events.map((e, i) => (
            <EventLine key={i} e={e} />
          ))}
        </ul>
      ) : (
        <div className="space-y-0.5 text-xs text-[var(--color-text-muted)]">
          <p>Żaden kamień nie zmienił stanu i żadne zadanie nie zostało wykonane.</p>
          {p.added_only_count > 0 && (
            <p>
              Dodano {p.added_only_count} {plural(p.added_only_count, "zadanie", "zadania", "zadań")} — bez
              wykonania.
            </p>
          )}
        </div>
      )}

      {showAvailable && p.available.length > 0 && (
        <div className="mt-3">
          <div className="font-mono text-[0.65rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
            Można robić — piłka u nas ({p.available.length})
          </div>
          <ul className="mt-1 space-y-1">
            {p.available.map((a) => (
              <li
                key={a.key}
                className="pl-2.5 font-mono text-[11px] text-[var(--color-text-secondary)]"
                style={{ borderLeft: "3px solid var(--color-border-strong)" }}
              >
                <b className="font-semibold">{a.label}</b>
                {a.since != null && a.days != null ? (
                  <>
                    <span className="text-[var(--color-text-muted)]">
                      {" "}· odblokowane {a.since_exact ? "od" : "najpóźniej od"} {formatDate(a.since)} ·{" "}
                    </span>
                    {/* Szacunek (niepełne daty domknięć) nigdy nie idzie na czerwono —
                        czerwień to zarzut, a tej liczby nie da się obronić. */}
                    <span
                      className={
                        a.since_exact && a.days >= 14
                          ? "font-bold text-[var(--color-primary)]"
                          : "text-[var(--color-text-muted)]"
                      }
                      title={
                        a.since_exact
                          ? undefined
                          : "Część poprzedników domknięto bez zapisanej daty — to dolne ograniczenie, nie dokładny czas."
                      }
                    >
                      {a.since_exact ? "" : "co najwyżej "}
                      {dayCount(a.days)} bez ruchu
                    </span>
                  </>
                ) : (
                  <span className="text-[var(--color-text-muted)]">
                    {" "}· odblokowane (chwila odblokowania nieustalona)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.open_directives.length > 0 && (
        <div className="mt-3 border-t border-[var(--color-border)] pt-2.5">
          <div
            className="font-mono text-[0.65rem] font-bold uppercase tracking-widest"
            style={{ color: "var(--color-primary)" }}
          >
            Nie zostało zrobione — ustalenia dyrekcji ({p.open_directives.length})
          </div>
          <ul className="mt-1 space-y-1">
            {p.open_directives.map((o, i) => (
              <li
                key={i}
                className="pl-2.5 text-xs text-[var(--color-text-primary)]"
                style={{ borderLeft: "3px solid var(--color-primary)" }}
              >
                {o.description}
                <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                  {" "}· dodane {formatDate(o.created_at)} · termin {formatDate(o.due_date)}
                </span>
                {o.overdue_days != null && o.overdue_days > 0 && (
                  <span className="font-mono text-[11px] text-[var(--color-primary)]">
                    {" "}· {dayCount(o.overdue_days)} po terminie
                  </span>
                )}
                {o.due_moves > 0 && (
                  <span className="font-mono text-[11px]" style={{ color: AMBER }}>
                    {" "}· przesuwany ×{o.due_moves} *
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      </div>
    </div>
  );
}

function ProjectsPanel({ projects }: { projects: ChangeAnalysisProject[] }) {
  const [showAvailable, setShowAvailable] = useState(true);
  // Kolejność nazwami — spotkanie idzie po liście projektów, a nie po tym, gdzie
  // akurat było najwięcej ruchu.
  const ordered = useMemo(
    () => [...projects].sort((a, b) => byProjectName(a.name, b.name)),
    [projects],
  );
  const availableTotal = projects.reduce((n, p) => n + p.available.length, 0);
  return (
    <section className="card mb-4">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-2">
        <h2 className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
          Co się zmieniło — projektami
        </h2>
        <button
          type="button"
          onClick={() => setShowAvailable((v) => !v)}
          title="Kroki z domkniętymi poprzednikami, których nikt jeszcze nie ruszył — piłka po naszej stronie"
          className={
            showAvailable
              ? "border border-[var(--color-text-primary)] bg-[var(--color-text-primary)] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-inverse)]"
              : "border border-[var(--color-border-strong)] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)]"
          }
        >
          {showAvailable ? "✓ " : ""}Odblokowane kamienie{availableTotal > 0 ? ` (${availableTotal})` : ""}
        </button>
      </div>
      {ordered.length === 0 ? (
        <p className="p-4 text-xs text-[var(--color-text-muted)]">Brak projektów w oknie analizy.</p>
      ) : (
        <div className="space-y-3 bg-[var(--color-bg-base)] p-3">
          {ordered.map((p) => (
            <ProjectBlock key={p.connection_id} p={p} showAvailable={showAvailable} />
          ))}
        </div>
      )}
    </section>
  );
}

// ── weekly activity bars ──────────────────────────────────────────────────────
function WeekBars({
  title,
  weeks,
  field,
  color,
}: {
  title: string;
  weeks: ChangeAnalysisWeek[];
  field: "transitions" | "tasks_done";
  color: string;
}) {
  const max = Math.max(1, ...weeks.map((w) => w[field]));
  return (
    <div className="card p-4">
      <h3 className="section-label mb-3">{title}</h3>
      {weeks.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">Brak danych w tym oknie.</p>
      ) : (
        <div className="flex items-end gap-1.5 overflow-x-auto pb-1">
          {weeks.map((w) => {
            const v = w[field];
            return (
              <div
                key={w.monday}
                className="flex min-w-[52px] flex-1 flex-col items-center"
                title={`${weekRangeLabel(w.monday)}: ${v}`}
              >
                <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">{v}</span>
                <div className="flex h-24 w-full items-end px-1.5">
                  <div
                    className="w-full"
                    style={{ height: `${(v / max) * 100}%`, background: color, minHeight: v > 0 ? 2 : 0 }}
                  />
                </div>
                <span className="mt-1 whitespace-nowrap font-mono text-[10px] text-[var(--color-text-muted)]">
                  {weekRangeLabel(w.monday)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── „Stan kamieni" ────────────────────────────────────────────────────────────
/** "Zmiany w oknie": transitions, or the reason there are none. */
function WindowChangesCell({ m }: { m: ChangeAnalysisMilestone }) {
  if (m.transitions.length > 0) {
    return (
      <ul className="space-y-1">
        {m.transitions.map((t, i) => (
          <li
            key={i}
            className="pl-2 font-mono text-[11px] text-[var(--color-text-primary)]"
            style={{ borderLeft: `3px solid ${t.forward ? GREEN : "var(--color-primary)"}` }}
          >
            <span className="text-[var(--color-text-muted)]">
              {formatDate(t.at)} · {t.by} ·{" "}
            </span>
            <StatePill dense state={t.from} label={t.from_display} /> →{" "}
            <StatePill dense state={t.to} label={t.to_display} />
            {t.note && <div className="text-[10px] text-[var(--color-text-muted)]">„{t.note}”</div>}
          </li>
        ))}
      </ul>
    );
  }
  if (m.state_now === "done" || m.state_now === "na") {
    return <span className="text-[var(--color-text-muted)]">—</span>;
  }
  if (m.state_now === "submitted") {
    const over = m.sla_days != null && m.waiting_days != null && m.waiting_days > m.sla_days;
    return (
      <span className="font-mono text-[11px]" style={{ color: AMBER }}>
        czeka: {m.waiting_label ?? "strona zewnętrzna"} — {dayCount(m.waiting_days ?? m.in_state_days)}
        {over && <span className="text-[var(--color-primary)]"> · SLA {m.sla_days} przekroczone</span>}
      </span>
    );
  }
  if (m.state_now === "applicable") {
    return <span className="font-mono text-[11px] text-[var(--color-text-muted)]">oznaczono: dotyczy</span>;
  }
  if (m.state_now === "in_progress") {
    const days = m.stalled_days ?? m.in_state_days;
    return (
      <span
        className={`font-mono text-[11px] ${
          days >= 14 ? "font-bold text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"
        }`}
      >
        bez zmian — {dayCount(days)}
      </span>
    );
  }
  return <span className="font-mono text-[11px] text-[var(--color-text-muted)]">nie rozpoczęto</span>;
}

function MilestoneRow({ m, onOpen }: { m: ChangeAnalysisMilestone; onOpen: () => void }) {
  const slaOver = m.sla_days != null && m.waiting_days != null && m.waiting_days > m.sla_days;
  return (
    <tr
      className="cursor-pointer border-t border-[var(--color-border-muted)] align-top hover:bg-[var(--color-bg-elevated)]"
      onClick={onOpen}
      title="Otwórz kamień — podgląd i zmiana stanu"
    >
      <td className="max-w-[240px] px-3 py-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="text-left font-semibold text-[var(--color-text-primary)] underline decoration-transparent underline-offset-2 hover:decoration-[var(--color-primary)]"
        >
          {m.label}
        </button>
        <div className="font-mono text-[10px] text-[var(--color-text-muted)]">{m.key}</div>
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <StatePill state={m.state_before} label={m.state_before_display} />
      </td>
      <td className="px-1 py-2 text-[var(--color-text-muted)]">→</td>
      <td className="whitespace-nowrap px-3 py-2">
        <StatePill state={m.state_now} label={m.state_now_display} />
      </td>
      <td className="min-w-[220px] px-3 py-2">
        <WindowChangesCell m={m} />
      </td>
      <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-[var(--color-text-muted)]">
        {formatDate(m.in_state_since)}
        <div className="text-[10px]">{dayCount(m.in_state_days)}</div>
      </td>
      <td className="max-w-[180px] px-3 py-2">
        {m.waiting_label ? (
          <div>
            <div className="text-[var(--color-text-primary)]">{m.waiting_label}</div>
            {m.waiting_days != null && (
              <div
                className={`font-mono text-[10px] ${
                  slaOver ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"
                }`}
              >
                {dayCount(m.waiting_days)}
                {m.sla_days != null ? ` · SLA ${m.sla_days}` : ""}
              </div>
            )}
          </div>
        ) : (
          <span className="text-[var(--color-text-muted)]">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        {m.due_date ? (
          <div>
            <div className={m.overdue_days ? "text-[var(--color-primary)]" : ""}>{formatDate(m.due_date)}</div>
            {m.overdue_days != null && m.overdue_days > 0 && (
              <div className="font-mono text-[10px] text-[var(--color-primary)]">
                {dayCount(m.overdue_days)} po terminie
              </div>
            )}
          </div>
        ) : (
          <span className="text-[var(--color-text-muted)]">—</span>
        )}
      </td>
    </tr>
  );
}

/** Otwiera TEN SAM modal co tablica kamieni projektu (jedno miejsce edycji stanu).
 *  Modal chce pełny projekt + kamień, a analiza ma tylko lekkie wiersze, więc projekt
 *  dociągamy na żądanie. Po każdym zapisie modal unieważnia dane projektu — łapiemy to
 *  i przeliczamy analizę, żeby tabela pod spodem zmieniła się bez przeładowania. */
function MilestoneModal({
  connectionId,
  milestoneKey,
  onClose,
}: {
  connectionId: string;
  milestoneKey: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const conn = useConnection(connectionId);
  const firstStamp = useRef<number | null>(null);
  const stamp = conn.dataUpdatedAt;
  useEffect(() => {
    if (!stamp) return;
    if (firstStamp.current === null) {
      firstStamp.current = stamp; // pierwsze wczytanie to nie zmiana
      return;
    }
    if (stamp !== firstStamp.current) {
      firstStamp.current = stamp;
      qc.invalidateQueries({ queryKey: ["pp", "change-analysis"] });
    }
  }, [stamp, qc]);

  const milestone = conn.data?.milestones?.find((m) => m.key === milestoneKey);
  if (!conn.data || !milestone) return null;
  return <MilestoneDetailModal connection={conn.data} milestone={milestone} onClose={onClose} />;
}

function MilestonesTable({ data, sinceDay }: { data: ChangeAnalysisResult; sinceDay: string }) {
  // Default: only rows that actually moved in the window — the audit view of
  // everything else is one click away.
  const [onlyChanges, setOnlyChanges] = useState(true);
  const [detail, setDetail] = useState<{ connectionId: string; key: string } | null>(null);
  const groups = useMemo(() => {
    const byId = new Map(data.projects.map((p) => [p.connection_id, p]));
    const out = new Map<string, ChangeAnalysisMilestone[]>();
    for (const m of data.milestones) {
      // "Change" = a non-tidy transition — the same predicate the backend uses for
      // the timeline/proofs/weekly bars, so the filtered table never contradicts them.
      if (onlyChanges && !m.transitions.some((t) => !t.tidy)) continue;
      const list = out.get(m.connection_id) ?? [];
      list.push(m);
      out.set(m.connection_id, list);
    }
    return [...out.entries()]
      .map(([cid, rows]) => ({ cid, project: byId.get(cid), rows }))
      .sort((a, b) => byProjectName(a.project?.name ?? "", b.project?.name ?? ""));
  }, [data.projects, data.milestones, onlyChanges]);
  // What the default filter is holding back — so a tidy-only change is never
  // silently absent from the page.
  const shown = groups.reduce((n, g) => n + g.rows.length, 0);
  const hidden = data.milestones.length - shown;
  const tidyHidden = data.milestones.filter(
    (m) => m.transitions.length > 0 && !m.transitions.some((t) => !t.tidy),
  ).length;
  if (data.milestones.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="section-label m-0">Stan kamieni</h2>
        <button
          type="button"
          onClick={() => setOnlyChanges((v) => !v)}
          className={
            onlyChanges
              ? "border border-[var(--color-text-primary)] bg-[var(--color-text-primary)] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-inverse)]"
              : "border border-[var(--color-border-strong)] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)]"
          }
        >
          {onlyChanges ? "✓ " : ""}Pokaż jedynie zmiany
        </button>
      </div>
      {onlyChanges && hidden > 0 && (
        <p className="mb-2 font-mono text-[11px] text-[var(--color-text-muted)]">
          Ukryto {hidden} {plural(hidden, "kamień", "kamienie", "kamieni")} bez zmian
          {tidyHidden > 0 && (
            <>
              {" "}(w tym {tidyHidden} tylko z oznaczeniem dotyczy / nie dotyczy)
            </>
          )}
          .
        </p>
      )}
      {groups.length === 0 ? (
        <EmptyState>Brak zmian kamieni w tym okresie — wyłącz filtr, aby zobaczyć pełny obraz.</EmptyState>
      ) : (
      <TableShell>
        <thead>
          <tr>
            <Th>Kamień</Th>
            <Th>Stan {sinceDay}</Th>
            <Th className="w-6" />
            <Th>Stan dziś</Th>
            <Th>Zmiany w oknie</Th>
            <Th>W stanie od</Th>
            <Th>Czeka na</Th>
            <Th>Termin</Th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.cid}>
              <tr className="bg-[var(--color-bg-elevated)]">
                <td colSpan={8} className="px-3 py-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                    {g.project?.name ?? "—"}
                  </span>
                  {g.project?.contract_number && (
                    <span className="ml-2 font-mono text-[11px] text-[var(--color-text-muted)]">
                      {g.project.contract_number}
                    </span>
                  )}
                </td>
              </tr>
              {g.rows.map((m) => (
                <MilestoneRow
                  key={`${g.cid}:${m.key}`}
                  m={m}
                  onOpen={() => setDetail({ connectionId: g.cid, key: m.key })}
                />
              ))}
            </Fragment>
          ))}
        </tbody>
      </TableShell>
      )}
      {detail && (
        <MilestoneModal
          connectionId={detail.connectionId}
          milestoneKey={detail.key}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

// ── „Zadania" ─────────────────────────────────────────────────────────────────
function TaskStatusCell({ t }: { t: ChangeAnalysisTask }) {
  return (
    <div className="space-y-0.5 text-xs">
      {t.status === "done" ? (
        <div style={{ color: GREEN }}>
          wykonane{t.done_at ? ` ${formatDate(t.done_at)}` : ""}
          {t.duration_days != null ? ` · ${afterDays(t.duration_days)}` : ""}
        </div>
      ) : (
        <span className="inline-block border border-[var(--color-border-strong)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)]">
          {t.status_display}
        </span>
      )}
      {t.status === "done" && t.late_days != null && t.late_days > 0 && (
        <div className="font-mono text-[10px] text-[var(--color-primary)]">{dayCount(t.late_days)} po terminie</div>
      )}
      {t.status !== "done" && t.overdue_days != null && t.overdue_days > 0 && (
        <div className="font-mono text-[10px] text-[var(--color-primary)]">
          {dayCount(t.overdue_days)} po terminie
        </div>
      )}
      {t.due_moves > 0 && (
        <div className="font-mono text-[10px]" style={{ color: AMBER }}>
          termin przesuwany ×{t.due_moves} *
        </div>
      )}
    </div>
  );
}

type TaskSortKey = "created" | "due" | "status";

/** Sortowalny nagłówek zadań. Lokalny, bo dzieli styl z `Th` tego modułu (shared
 *  TableSort ma własną paletę i klikalne `<th>` bez obsługi klawiatury). Module-scope,
 *  żeby klik nie przemontowywał nagłówka i nie gubił fokusu. */
function TaskSortTh({
  field,
  label,
  sortKey,
  sortDesc,
  onSort,
}: {
  field: TaskSortKey;
  label: string;
  sortKey: TaskSortKey;
  sortDesc: boolean;
  onSort: (f: TaskSortKey) => void;
}) {
  const active = sortKey === field;
  return (
    <Th className="p-0">
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={`Sortuj: ${label}`}
        className="flex w-full items-center gap-1 px-3 py-2 text-left uppercase tracking-[inherit] hover:text-[var(--color-text-primary)]"
      >
        {label}
        <span aria-hidden className={active ? "text-[var(--color-primary)]" : "opacity-30"}>
          {active ? (sortDesc ? "↓" : "↑") : "↕"}
        </span>
      </button>
    </Th>
  );
}

function TasksTable({ data }: { data: ChangeAnalysisResult }) {
  const projectName = useMemo(
    () => new Map(data.projects.map((p) => [p.connection_id, p.name])),
    [data.projects],
  );
  const [sortKey, setSortKey] = useState<TaskSortKey>("created");
  const [sortDesc, setSortDesc] = useState(false);
  const toggleSort = (k: TaskSortKey) => {
    if (k === sortKey) setSortDesc((v) => !v);
    else {
      setSortKey(k);
      setSortDesc(false);
    }
  };
  // Grupujemy PROJEKTAMI (tak idzie spotkanie), a rodzaj — ustalenia dyrekcji przed
  // własnymi — jest pierwszym kluczem sortowania wewnątrz projektu.
  const groups = useMemo(() => {
    const cmp = (a: ChangeAnalysisTask, b: ChangeAnalysisTask) => {
      let r = 0;
      if (sortKey === "due") {
        // No due date last — "do następnego KPI" must not shadow dated work.
        if (a.due_date === b.due_date) r = 0;
        else if (a.due_date == null) r = 1;
        else if (b.due_date == null) r = -1;
        else r = a.due_date.localeCompare(b.due_date);
      } else if (sortKey === "created") {
        r = a.created_at.localeCompare(b.created_at);
      } else {
        // Status: overdue first, then open, done last (by completion date).
        const rank = (t: ChangeAnalysisTask) =>
          t.status === "done" ? 2 : t.overdue_days ? 0 : 1;
        r = rank(a) - rank(b) || (a.done_at ?? "").localeCompare(b.done_at ?? "");
      }
      if (r === 0) r = a.created_at.localeCompare(b.created_at);
      return sortDesc ? -r : r;
    };
    const byOriginThen = (a: ChangeAnalysisTask, b: ChangeAnalysisTask) =>
      Number(b.by_directorate) - Number(a.by_directorate) || cmp(a, b);

    const buckets = new Map<string, ChangeAnalysisTask[]>();
    for (const t of data.tasks) {
      const list = buckets.get(t.connection_id) ?? [];
      list.push(t);
      buckets.set(t.connection_id, list);
    }
    return [...buckets.entries()]
      .map(([cid, rows]) => ({
        cid,
        name: projectName.get(cid) ?? "—",
        rows: rows.sort(byOriginThen),
        directives: rows.filter((t) => t.by_directorate).length,
      }))
      .sort((a, b) => byProjectName(a.name, b.name));
  }, [data.tasks, projectName, sortKey, sortDesc]);
  if (data.tasks.length === 0) return null;

  const sortProps = { sortKey, sortDesc, onSort: toggleSort };
  return (
    <div className="mb-4">
      <h2 className="section-label mb-2">Zadania</h2>
      <TableShell>
        <thead>
          <tr>
            <Th>Zadanie</Th>
            <TaskSortTh field="created" label="Dodane" {...sortProps} />
            <TaskSortTh field="due" label="Termin" {...sortProps} />
            <TaskSortTh field="status" label="Status" {...sortProps} />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.cid}>
              {/* Gruba, ciemna belka — granica projektu ma być widoczna od razu. */}
              <tr className="border-t-4 border-[var(--color-text-primary)] bg-[var(--color-bg-elevated)]">
                <td colSpan={4} className="px-3 py-2">
                  <span className="text-xs font-bold uppercase tracking-wide text-[var(--color-text-primary)]">
                    {g.name}
                  </span>
                  <span className="ml-2 font-mono text-[11px] text-[var(--color-text-muted)]">
                    {g.rows.length} {plural(g.rows.length, "zadanie", "zadania", "zadań")} ·{" "}
                    {g.directives} z dyrekcji · {g.rows.length - g.directives}{" "}
                    {plural(g.rows.length - g.directives, "własne", "własne", "własnych")}
                  </span>
                </td>
              </tr>
              {g.rows.map((t) => {
                const done = t.status === "done";
                const late = !done && t.overdue_days != null && t.overdue_days > 0;
                return (
                <tr
                  key={t.id}
                  // Lewa krawędź niesie POCHODZENIE (ciemna = ustalenie dyrekcji,
                  // fioletowa = własne), tło — stan wykonania.
                  className={`border-t border-[var(--color-border-muted)] align-top ${
                    done ? "bg-[rgba(5,150,105,0.05)]" : late ? "bg-[rgba(220,31,31,0.05)]" : ""
                  }`}
                  style={{
                    borderLeft: `3px solid ${
                      t.by_directorate ? "var(--color-text-primary)" : "#7c3aed"
                    }`,
                  }}
                >
                  <td className="max-w-[380px] px-3 py-2">
                    <div
                      className={
                        done
                          ? "text-[var(--color-text-muted)] line-through decoration-[var(--color-border-strong)]"
                          : "text-[var(--color-text-primary)]"
                      }
                    >
                      {t.description}
                    </div>
                    {/* Projekt jest już w belce grupy — w wierszu liczy się POCHODZENIE. */}
                    <div className="mt-0.5">
                      <OriginBadge directorate={t.by_directorate} standalone />
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-[var(--color-text-muted)]">
                    {formatDate(t.created_at)} · {t.created_by_name}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {t.due_date ? formatDate(t.due_date) : <span className="text-[var(--color-text-muted)]">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <TaskStatusCell t={t} />
                  </td>
                </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </TableShell>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export function KpiChangeAnalysisPage() {
  const [personId, setPersonId] = useState("");
  const [since, setSince] = useState(() => daysAgoISO(7));
  const [sinceTime, setSinceTime] = useState("00:00");
  // What the time input shows while being edited; committed to sinceTime only when
  // complete — browsers emit "" for a half-typed value, which must not snap to 00:00.
  const [timeDraft, setTimeDraft] = useState("00:00");
  const [chip, setChip] = useState<number | null>(7);

  const people = useChangeAnalysisPeople();
  const peopleOptions = useMemo(
    () =>
      (people.data?.people ?? []).map((p) => ({
        value: p.id,
        label: `${p.name} — ${p.projects} ${plural(p.projects, "projekt", "projekty", "projektów")}`,
      })),
    [people.data],
  );

  const params = useMemo(
    () => (personId && since ? { person_id: personId, since, since_time: sinceTime } : null),
    [personId, since, sinceTime],
  );
  const analysis = useChangeAnalysis(params);
  const data = analysis.data;
  // The people query fires immediately, so a non-director hitting the URL gets the
  // access message even though the analysis query never ran.
  const is403 = (err: unknown) => (err as AxiosError | undefined)?.response?.status === 403;
  const forbidden = (people.isError && is403(people.error)) || (analysis.isError && is403(analysis.error));
  // Backend 400s carry an actionable Polish message in errors.detail — surface it.
  const backendDetail = (
    analysis.error as AxiosError<{ errors?: { detail?: string } }> | undefined
  )?.response?.data?.errors?.detail;

  const pickChip = (n: number) => {
    setChip(n);
    setSince(daysAgoISO(n));
    setSinceTime("00:00");
    setTimeDraft("00:00");
  };

  return (
    <div>
      <PageHeader
        title="Analiza postępów"
        subtitle="porównanie postępów w projektach ZE względem wybranej chwili — widok dyrekcji technicznej"
      />

      {/* ── controls ── */}
      <div className="card mb-4 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(240px,340px)_1fr]">
          <div>
            {/* Not a <label>: it would implicitly target SearchableSelect's hidden
                sr-only <select> and hijack clicks away from the custom trigger. */}
            <span className="section-label">Osoba</span>
            <div className="mt-1">
              <SearchableSelect
                options={peopleOptions}
                value={personId}
                placeholder="— wybierz osobę —"
                onChange={(e) => setPersonId(e.target.value)}
              />
            </div>
          </div>
          <div>
            <span className="section-label">Porównaj od</span>
            {/* One compact row: chips, date, time, effective-window label. The time
                input must not inherit input-base's full width (it ate a whole row). */}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
              {QUICK_CHIPS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => pickChip(n)}
                  className={
                    chip === n
                      ? "border border-[var(--color-text-primary)] bg-[var(--color-text-primary)] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[var(--color-text-inverse)]"
                      : "border border-[var(--color-border-strong)] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)] hover:text-[var(--color-text-primary)]"
                  }
                >
                  {n} dni
                </button>
              ))}
              <DatePicker
                value={since}
                onChange={(v) => {
                  if (!v) return;
                  setSince(v);
                  setChip(null);
                }}
                max={localISO(new Date())}
                clearable={false}
                className="w-[150px]"
                aria-label="Data początku okna"
              />
              <input
                type="time"
                className="input-base !w-[108px] shrink-0 text-center font-mono"
                title="Godzina odniesienia — domyślnie 00:00 (początek dnia)"
                value={timeDraft}
                onChange={(e) => {
                  setTimeDraft(e.target.value);
                  if (/^\d{2}:\d{2}$/.test(e.target.value)) {
                    setSinceTime(e.target.value);
                    setChip(null);
                  }
                }}
                onBlur={() => {
                  if (!/^\d{2}:\d{2}$/.test(timeDraft)) setTimeDraft(sinceTime);
                }}
                aria-label="Godzina początku okna"
              />
              <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                od {ddmmyyyy(since)} {sinceTime} → dziś
              </span>
              {analysis.isFetching && <Spinner size="sm" />}
            </div>
          </div>
        </div>
        {data && data.meta.excluded_shelved_or_closed > 0 && (
          <p className="mt-3 font-mono text-[11px] text-[var(--color-text-muted)]">
            Poza rozliczeniem: {data.meta.excluded_shelved_or_closed}{" "}
            {plural(data.meta.excluded_shelved_or_closed, "projekt", "projekty", "projektów")} ze statusem
            Półka / Zamknięty.
          </p>
        )}
        {personId && data && (
          <PresenceBar
            personId={personId}
            personName={data.person.name}
            sinceISO={data.window.since.slice(0, 10)}
            untilISO={localISO(new Date())}
          />
        )}
      </div>

      {forbidden ? (
        <EmptyState>Widok dostępny wyłącznie dla dyrekcji technicznej.</EmptyState>
      ) : analysis.isError ? (
        <EmptyState>{backendDetail ?? "Nie udało się pobrać analizy — spróbuj ponownie."}</EmptyState>
      ) : analysis.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : !params ? (
        <EmptyState>Wybierz osobę, aby zobaczyć rozliczenie okresu.</EmptyState>
      ) : data ? (
        <>
          <VerdictPanel data={data} />
          <ProjectsPanel projects={data.projects} />
          <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <WeekBars
              title="Zmiany stanów kamieni — tygodniami"
              weeks={data.weeks}
              field="transitions"
              color="#dc1f1f"
            />
            <WeekBars
              title="Zadania wykonane — tygodniami"
              weeks={data.weeks}
              field="tasks_done"
              color="#404040"
            />
          </div>
          <MilestonesTable data={data} sinceDay={formatDate(data.window.since)} />
          <TasksTable data={data} />
          <section className="card mb-4 p-4">
            <h2 className="section-label mb-2">Skąd te dane</h2>
            <ul className="list-disc space-y-1 pl-5 text-xs text-[var(--color-text-secondary)]">
              <li>
                Kamienie: pełna historia zmian stanów od startu modułu — kto, kiedy, z jakiego stanu, a przy
                cofnięciu także powód.
              </li>
              <li>
                Zadania: daty dodania i wykonania od zawsze; rejestr zmian terminów i statusów przyrasta od
                dnia wdrożenia — stąd gwiazdka (*) przy „termin przesuwany”.
              </li>
              <li>
                Kamień „Wysłane” czeka po stronie urzędu/ZE — kolumna „Czeka na” oddziela to oczekiwanie od
                braku postępu.
              </li>
            </ul>
          </section>
        </>
      ) : null}
    </div>
  );
}
